import {
  Edit02Icon,
  PauseIcon,
  PlayIcon,
  ViewIcon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import React, { useState } from "react";
import { Stack } from "react-bootstrap";
import { useLocalizedText } from "../../utils/hooks/useLocalizedText";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../utils/helpers";
import { useScreenSize } from "../../utils/hooks/useScreenSize ";
import { Tag, Tooltip } from "antd";
import { Link } from "react-router-dom";
import ActionDropdown from "../../CommonElements/ActionDropdown";
import { useUserStatusUpdateMutation } from "../../services/user";
import ConfirmModal from "../../CommonElements/ConfirmModal";
import { DisplayGroupsModal } from "./DisplayGroupsModal";
import { Trans } from "react-i18next";
import { usePermissions } from "../../utils/hooks/usePermissions";
import logo from "@assets/svgs/logo.svg";
import { permissions } from "@/utils/constants";
import { Can } from "@/CommonElements/PermissionGate";

export const UserCard = ({ loading = false, userData }) => {
  const { getLocalizedText } = useLocalizedText();
  const { t, i18n } = useTranslation();
  const { isTablet, isMobile } = useScreenSize();
  const {
    hasAnyPermission
  } = usePermissions();
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const { mutate: suspendUser, isPendin: isSuspendUserPending } =
    useUserStatusUpdateMutation();
  const [showGroupsModal, setShowGroupsModal] = useState(false);
  const handleShowGroupsModal = (e) => {
    e.preventDefault();
    setShowGroupsModal(true);
  };

  const handleCloseGroupsModal = () => {
    setShowGroupsModal(false);
  };

  // Helper function to handle responsive text display
  const ResponsiveText = ({ text, className = "" }) => {
    // On desktop: use tooltip with truncation
    if (!isMobile && !isTablet) {
      return (
        <Tooltip title={text} placement="top">
          <span className={`${className} text-truncate`}>{text}</span>
        </Tooltip>
      );
    }

    // On tablet and mobile: show full content without truncation
    return <span className={className}>{text}</span>;
  };

  const handleSuspendUser = () => {
    setShowSuspendModal(true);
  };

  const confirmSuspendUser = () => {
    if (!userData?.id) return;

    suspendUser(
      { id: userData?.id, is_active: userData?.is_active },
      {
        onSuccess: () => {
          setShowSuspendModal(false);
        },
        onError: () => {
          setShowSuspendModal(false);
        },
      }
    );
  };

  const cancelSuspendUser = () => {
    setShowSuspendModal(false);
  };

  // Check permissions
  const canView = hasAnyPermission([permissions.list_users_for_user_management]);
  const canEdit = hasAnyPermission([permissions.update_user_for_user_management]);
  const canSuspend = hasAnyPermission([permissions.update_user_status_for_user_management]);

  const items = [
    // Only add View item if user has permission
    ...(canView
      ? [
        {
          label: (
            <Link
              to={`${userData?.id}/profile`}
              className="d-flex align-items-center gap-2"
            >
              <HugeiconsIcon size={18} icon={ViewIcon} />
              <span>{t("VIEW")}</span>
            </Link>
          ),
          key: "View",
        },
      ]
      : []),

    // Only add Edit item if user has permission
    ...(canEdit
      ? [
        {
          label: (
            <Link
              to={`${userData?.id}/edit`}
              className="d-flex align-items-center gap-2"
            >
              <HugeiconsIcon size={18} icon={Edit02Icon} />
              <span>{t("EDIT")}</span>
            </Link>
          ),
          key: "EDIT",
        },
      ]
      : []),

    // Only add divider if we have items above and below
    ...(canView || canEdit ? [{ type: "divider" }] : []),

    // Only add Suspend/Activate item if user has permission
    ...(canSuspend
      ? [
        {
          label: (
            <a
              className="d-flex align-items-center gap-2"
              onClick={handleSuspendUser}
            >
              {userData?.is_active ? (
                <HugeiconsIcon size={18} icon={PauseIcon} />
              ) : (
                <HugeiconsIcon size={18} icon={PlayIcon} />
              )}
              <span>
                {userData?.is_active ? t("SUSPEND") : t("ACTIVATE")}
              </span>
            </a>
          ),
          key: "SUSPEND",
          style: {
            color: "#fff",
            backgroundColor: userData?.is_active ? "#dc3545" : "#28a745",
          },
        },
      ]
      : []),
  ].filter((item) => item !== null); // Remove any null items

  if (loading) {
    return (
      <div className="card mb-2 shadow-sm">
        <div className="card-body">
          <div className="row g-3 align-items-center">
            {/* User Info Column */}
            <div className="col-12 border-bottom pb-1">
              <Stack
                direction="horizontal"
                gap={2}
                className="H5 justify-content-between"
              >
                <Stack direction="horizontal" gap={2}>
                  <div className="skeleton skeleton-avatar"></div>
                  <div className="skeleton skeleton-name"></div>
                </Stack>

                {/* Actions Column for mobile only - loading state */}
                <div className="skeleton skeleton-menu-btn"></div>
              </Stack>
            </div>

            {/* National ID Column */}
            <div className="col-xl-2 col-lg-3 col-md-4 col-6">
              <div className="skeleton-field">
                <div className="skeleton skeleton-label"></div>
                <div className="skeleton skeleton-value"></div>
              </div>
            </div>
            {/* National ID Column */}
            <div className="col-xl-2 col-lg-3 col-md-4 col-6">
              <div className="skeleton-field">
                <div className="skeleton skeleton-label"></div>
                <div className="skeleton skeleton-value"></div>
              </div>
            </div>

            {/* Email Column */}
            <div className="col-xl-3 col-lg-4 col-md-4 col-6">
              <div className="skeleton-field">
                <div className="skeleton skeleton-label"></div>
                <div className="skeleton skeleton-value email"></div>
              </div>
            </div>

            {/* Status Column */}
            <div className="col-xl-3 col-lg-4 col-md-4 col-6">
              <div className="skeleton-field">
                <div className="skeleton skeleton-label"></div>
                <div className="skeleton skeleton-value email"></div>
              </div>
            </div>

            {/* Group Column */}
            <div className="col-xl-2 col-lg-3 col-md-4 col-6">
              <div className="skeleton-field">
                <div className="skeleton skeleton-label"></div>
                <div className="skeleton skeleton-value group"></div>
              </div>
            </div>

            {/* Gender Column */}
            <div className="col-xl-2 col-lg-auto col-md-4 col-6">
              <div className="skeleton-field">
                <div className="skeleton skeleton-label"></div>
                <div className="skeleton skeleton-value"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card mb-2 shadow-sm">
      <div className="card-body p-4">
        <div className="row g-3 align-items-center">
          {/* User Info Column */}
          <div className="col-12 border-bottom pb-1">
            <Stack
              direction="horizontal"
              gap={2}
              className="H5 justify-content-between"
            >
              <Link
                to={`${userData?.id}/profile`}
                className="d-flex align-items-center gap-2"
              >
                <div
                  className="position-relative bg-white border"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "0.5rem",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <img
                    style={{ objectFit: "cover", objectPosition: "center" }}
                    className="img-fluid h-100 w-100"
                    src={userData.profile_image_url ?? logo}
                    alt=""
                  />
                </div>
                <div className={isMobile || isTablet ? "" : "text-truncate"}>
                  <ResponsiveText
                    text={getLocalizedText(userData.name_ar, userData.name_en)}
                  />
                </div>
              </Link>

              {/* Actions Column for mobile only */}
              <div className="H6 text-primary">
                <Can anyOf={[permissions.update_user_status_for_user_management, permissions.update_user_for_user_management]}>
                  <ActionDropdown
                    items={items}
                    buttonClassName="outline btn-icon"
                  />
                </Can>
              </div>
            </Stack>
          </div>

          {/* National ID Column */}
          <div className="col-xl-2 col-lg-3 col-md-4 col-6">
            <div className="H7 text-secondary mb-1">{t("NATIONAL_ID")}</div>
            <div
              className={`H6 text-primary ${isMobile || isTablet ? "" : "text-truncate"
                }`}
            >
              <ResponsiveText text={userData.national_id} />
            </div>
          </div>

          {/* Email Column */}
          <div className="col-xl-2 col-lg-4 col-md-4 col-6">
            <div className="H7 text-secondary mb-1">{t("EMAIL")}</div>
            <div
              className={`H6 text-primary ${isMobile || isTablet ? "" : "text-truncate"
                }`}
            >
              <ResponsiveText text={userData.email} />
            </div>
          </div>

          {/* Status Column */}
          <div className="col-xl-2 col-lg-3 col-md-4 col-6">
            <div className="H7 text-secondary mb-1">{t("STATUS")}</div>
            <div>
              <Tag color={userData?.is_active ? "green" : "red"}>
                {userData?.is_active ? t("ACTIVE") : t("SUSPENDED")}
              </Tag>
            </div>
          </div>

          {/* Group Column */}
          <div className="col-xl-2 col-lg-3 col-md-4 col-6">
            <div className="H7 text-secondary mb-1">{t("GROUP")}</div>
            {userData?.groups.length > 0 && (
              <a
                href=""
                className={`H6 d-flex align-items-center fw-bold cursor-pointer ${isMobile || isTablet ? "" : "text-truncate"
                  }`}
                onClick={handleShowGroupsModal}
              >
                <ResponsiveText
                  text={getLocalizedText(
                    userData?.groups?.[0].name_ar,
                    userData?.groups?.[0].name_en
                  )}
                  className={`me-1 ${isMobile || isTablet ? "" : "text-truncate"
                    }`}
                />

                {userData.groups?.length > 1 && (
                  <small>+{userData.groups.length - 1}</small>
                )}
              </a>
            )}
            {userData.groups?.length <= 0 && (
              <span className="text-muted">{t("NO_GROUP")}</span>
            )}

            <DisplayGroupsModal
              groups={userData.groups ?? []}
              open={showGroupsModal}
              onClose={handleCloseGroupsModal}
            />
          </div>
          {/* Service Center Column */}
          <div className="col-xl-2 col-lg-3 col-md-4 col-6">
            <div className="H7 text-secondary mb-1">{t("SERVICE_CENTER")}</div>
            <div
              className={`H6 text-primary ${isMobile || isTablet ? "" : "text-truncate"
                }`}
            >
              <ResponsiveText text={getLocalizedText(userData.service_center?.name_ar, userData.service_center?.name_en)} />
            </div>
          </div>

          {/* Employer Column */}
          <div className="col-xl-2 col-lg-3 col-md-4 col-6">
            <div className="H7 text-secondary mb-1">{t("EMPLOYER")}</div>
            <div
              className={`H6 text-primary ${isMobile || isTablet ? "" : "text-truncate"
                }`}
            >
              <ResponsiveText
                text={getLocalizedText(
                  userData?.job_destination?.name_ar,
                  userData?.job_destination?.name_en
                ) || "-"}
              />
            </div>
          </div>

          {/* Gender Column */}
          <div className="col-xl-2 col-lg-auto col-md-4 col-6">
            <div className="H7 text-secondary mb-1">{t("GENDER")}</div>
            <div className="H6 text-primary">
              <ResponsiveText text={t(userData?.gender?.toUpperCase())} />
            </div>
          </div>

          {/* Date Column */}
          <div className="col-xl-2 col-lg-auto col-md-4 col-6">
            <div className="H7 text-secondary mb-1">{t("JOINED_DATE")}</div>
            <div className="H6 text-primary">
              <ResponsiveText
                text={formatDate(userData?.created_at, i18n.language)}
              />
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal
        isOpen={showSuspendModal}
        onToggle={cancelSuspendUser}
        onConfirm={confirmSuspendUser}
        title={t("CONFIRM")}
        message={
          userData?.is_active ? (
            <Trans
              i18nKey="CONFIRM_SUSPEND_USER"
              components={{
                danger: <span className="text-danger" />,
              }}
            />
          ) : (
            <Trans
              i18nKey="CONFIRM_ACTIVATE_USER"
              components={{
                danger: <span className="text-success" />,
              }}
            />
          )
        }
        dangerText={userData?.is_active ? t("SUSPEND") : t("ACTIVATE")}
        confirmText={userData?.is_active ? t("SUSPEND") : t("ACTIVATE")}
        cancelText={t("CANCEL")}
        isLoading={isSuspendUserPending}
        defaultLoadingText={
          userData?.is_active ? t("SUSPENDING") : t("ACTIVATING")
        }
        loadingText={userData?.is_active ? t("SUSPENDING") : t("ACTIVATING")}
        confirmVariant={userData?.is_active ? "danger" : "success"}
        confirmIcon={userData?.is_active ? PauseIcon : PlayIcon}
      />
    </div>
  );
};
